// services/apiClient.ts
import { storage } from "@/utils/storage";
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { router } from "expo-router";

const BASE_URL = "https://backend.shreesetup.com/api";

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000,
});

// Request interceptor – attach token to every request EXCEPT login
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token attachment for login endpoint
    if (config.url?.includes("/auth/login")) {
      return config;
    }
    const token = await storage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor – handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const isLoginRequest = originalRequest.url?.includes("/auth/login");

    // Handle 401 Unauthorized – only for non-login requests (token expired)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isLoginRequest
    ) {
      originalRequest._retry = true;
      await storage.clearAuth();
      router.replace("/(auth)/login");
      return Promise.reject(new Error("Session expired. Please login again."));
    }

    // For login endpoint, 401 means invalid credentials – just pass the error message
    if (isLoginRequest && error.response?.status === 401) {
      const message =
        (error.response?.data as any)?.message || "Invalid email or password";
      return Promise.reject(new Error(message));
    }

    // Handle network errors
    if (error.message === "Network Error") {
      return Promise.reject(
        new Error("Network error. Please check your internet connection."),
      );
    }

    // Handle timeout
    if (error.code === "ECONNABORTED") {
      return Promise.reject(new Error("Request timeout. Please try again."));
    }

    // Pass other errors through
    if (error.response?.data) {
      const message =
        (error.response.data as any)?.message || "Something went wrong";
      return Promise.reject(new Error(message));
    }

    return Promise.reject(error);
  },
);

export default apiClient;
